import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Grid, GridColumn, GridCellProps, GridNoRecords } from '@progress/kendo-react-grid';
import { plusIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import { ExpansionPanel, ExpansionPanelContent } from '@progress/kendo-react-layout';
import { Reveal } from '@progress/kendo-react-animation';
import { RemoveCell } from '../../grid/customCells/RemoveCell';
import { EditableFacetsCountCell } from './userViews/privateView/EditableFacetsCountCell';
import { DragCell } from '../../grid/customCells/DragCell';
import { FacetCountInfo } from './FacetCountInfo';
import {
  useGlobalizationContext,
  useTranslation,
  useCurrentUserContext,
  useViewsContext,
  useGetAllFields,
  useGetGlobalFiltersLimit,
  DragContext
} from '@cs-cms/state-management';
import { CpsGlobalFilter } from '@cs-cms/types';

interface GlobalFiltersEditorProps {
  indexId?: number;
}

const GLOBAL_FILTER_COLUMNS = {
  ACTIONS: 'actions',
  FIELD: 'field',
  FACETS_COUNT: 'facetsCount'
} as const;

export const GlobalFiltersEditor: React.FC<GlobalFiltersEditorProps> = ({ indexId }) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { selectedIndex } = useCurrentUserContext();
  const resolvedIndexId = indexId ?? selectedIndex?.id;
  const { editingViewState, setEditingViewState, setHasUnsavedChanges } = useViewsContext();
  const { data: allFields } = useGetAllFields(selectedLocale, resolvedIndexId);
  const { data: globalFiltersLimit } = useGetGlobalFiltersLimit(resolvedIndexId);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [activeItem, setActiveItem] = React.useState<CpsGlobalFilter | null>(null);

  const updateFilters = (newFilters: CpsGlobalFilter[]) => {
    if (!setEditingViewState) {
      return;
    }

    setEditingViewState({
      ...editingViewState,
      globalFilters: newFilters
    });
  };

  const reorder = (dataItem: CpsGlobalFilter) => {
    if (activeItem === dataItem || !activeItem) {
      return;
    }

    const reorderedData = [...editingViewState.globalFilters];
    const prevIndex = reorderedData.findIndex(f => f.id === activeItem.id);
    const nextIndex = reorderedData.findIndex(f => f.id === dataItem.id);

    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(nextIndex, 0, activeItem);

    updateFilters(reorderedData);
  };

  const dragStart = (dataItem: CpsGlobalFilter) => {
    setActiveItem(dataItem);
  };

  const availableFields = allFields
    ? allFields
        .filter(field => !field.technical)
        .map(field => ({
          value: field.id,
          label: field.displayName || field.name,
          facetable: field.facetable
        }))
    : [];

  const addGlobalFilter = () => {
    const tempId = -(Date.now() + Math.random());
    const newFilter: CpsGlobalFilter = {
      id: tempId,
      name: '',
      type: 'text'
    };
    updateFilters([...editingViewState.globalFilters, newFilter]);
  };

  const updateGlobalFilter = (id: number, updates: Partial<CpsGlobalFilter>) => {
    const updatedFilters = editingViewState.globalFilters.map(filter => {
      if (filter.id === id) {
        const updated = { ...filter, ...updates };
        if (updates.id != null && updates.id !== id) {
          const fieldConfig = availableFields.find(f => f.value === updates.id);
          if (fieldConfig) {
            updated.name = fieldConfig.label;
          }
        }
        return updated;
      }
      return filter;
    });
    updateFilters(updatedFilters);
    setHasUnsavedChanges?.(true);
  };

  const getAvailableFields = (currentFieldId?: number) => {
    const usedFields = new Set(editingViewState.globalFilters.map(f => f.id));
    return availableFields.filter(field => !usedFields.has(field.value) || field.value === currentFieldId);
  };

  const isFieldIncomplete = (filter: CpsGlobalFilter) => {
    const selectedField = getAvailableFields(filter.id).find(f => f.value === filter.id);
    return filter.id < 0 || !selectedField;
  };

  const handleCellRender = (cellProps: GridCellProps) => {
    if (!cellProps.field) {
      return <td />;
    }

    const filter = cellProps.dataItem as CpsGlobalFilter;

    switch (cellProps.field) {
      case GLOBAL_FILTER_COLUMNS.FIELD: {
        const isIncomplete = isFieldIncomplete(filter);

        return (
          <td>
            <DropDownList
              data={getAvailableFields(filter.id)}
              textField="label"
              dataItemKey="value"
              onChange={e => updateGlobalFilter(filter.id, { id: e.value.value })}
              fillMode="solid"
              value={getAvailableFields(filter.id).find(f => f.value === filter.id) || null}
              style={{
                border: isIncomplete ? '1px solid red' : undefined,
                borderRadius: isIncomplete ? '4px' : undefined
              }}
            />
          </td>
        );
      }
      case GLOBAL_FILTER_COLUMNS.ACTIONS:
        return (
          <RemoveCell
            dataItem={cellProps.dataItem}
            field={cellProps.field}
            dataIndex={cellProps.dataIndex}
            id={cellProps.id}
            columnPosition={cellProps.columnPosition}
            onChange={cellProps.onChange}
            className={cellProps.className}
            colSpan={cellProps.colSpan}
            ariaColumnIndex={cellProps.ariaColumnIndex}
            isSelected={cellProps.isSelected}
            style={cellProps.style}
            rowType={cellProps.rowType}
            level={cellProps.level}
            expanded={cellProps.expanded}
            onClick={() => {
              const filteredFilters = editingViewState.globalFilters.filter(f => f.id !== filter.id);
              updateFilters(filteredFilters);
              setHasUnsavedChanges?.(true);
            }}
          />
        );

      default:
        return <td>{cellProps.dataItem[cellProps.field]}</td>;
    }
  };

  const panelTitle = `${translate('custom.settings.views.globalFilters.title')} (${editingViewState.globalFilters.length} ${translate('custom.settings.views.globalFilters.filtersCount')})`;
  const isAddDisabled =
    (globalFiltersLimit != null && editingViewState.globalFilters.length >= globalFiltersLimit) ||
    getAvailableFields().length === 0;

  return (
    <ExpansionPanel title={panelTitle} expanded={isExpanded} onAction={event => setIsExpanded(!event.expanded)}>
      <Reveal>
        {isExpanded && (
          <ExpansionPanelContent>
            <FacetCountInfo />
            <span
              title={isAddDisabled ? translate('custom.settings.views.globalFiltersLimitExceeded') : undefined}
              className="addGlobalFilterButton"
            >
              <Button
                onClick={addGlobalFilter}
                size="small"
                themeColor="primary"
                fillMode="flat"
                disabled={isAddDisabled}
              >
                <SvgIcon icon={plusIcon} />
                {translate('custom.settings.views.globalFilters.addFilter')}
              </Button>
            </span>

            <DragContext.Provider value={{ reorder, dragStart }}>
              <Grid
                data={editingViewState.globalFilters}
                dataItemKey="id"
                size={'small'}
                cells={{ data: handleCellRender }}
              >
                <GridNoRecords>{translate('custom.settings.views.globalFilters.noFilters')}</GridNoRecords>

                <GridColumn
                  title={translate('custom.settings.views.gridHeader.order')}
                  width={'150px'}
                  cells={{ data: DragCell }}
                />

                <GridColumn field="field" title={translate('custom.settings.views.globalFilters.field')} />

                <GridColumn
                  field="facetsCount"
                  title={translate('custom.settings.views.gridHeader.facetsCount')}
                  width={'200px'}
                  cells={{ edit: { numeric: EditableFacetsCountCell } }}
                />

                <GridColumn
                  field="actions"
                  title={translate('custom.settings.views.globalFilters.actions')}
                  width={'150px'}
                />
              </Grid>
            </DragContext.Provider>
          </ExpansionPanelContent>
        )}
      </Reveal>
    </ExpansionPanel>
  );
};
